import { OmniElement, html, nothing } from 'omni-ui';
import { om2confirm } from './om2-alert.js';
import {
  fetchEnumOptions,
  renderTooltip,
} from './om2-form-field/common/util.js';
import { Om2FormFieldBase } from './om2-form-field/common/base.js';
// Input fields
import './om2-form-field/string.js';
import './om2-form-field/string-multiline.js';
import './om2-form-field/hidden.js';
import './om2-form-field/email.js';
import './om2-form-field/number.js';
import './om2-form-field/boolean.js';
import './om2-form-field/date.js';
import './om2-form-field/radio.js';
import './om2-form-field/dropdown.js';
import './om2-form-field/two-pane.js';
import './om2-form-field/image.js';
import './om2-form-field/file.js';
import './om2-form-field/object.js';
import './om2-form-field/custom.js';
// Layout fields
import './om2-form-field/title.js';
import './om2-form-field/text.js';
import './om2-form-field/horizontal-rule.js';
import './om2-form-field/fixed-image.js';

/**
 * Renders form field types to be used with Om2FormField.
 * Individual field types are rendered by their respective component classes. Common
 * shared functionality like titles, tooltips, errors, omni-assist, and loading
 * external data is included in this class.
 *
 * @extends OmniElement
 */
export class Om2FormField extends OmniElement {
  constructor() {
    super();
    this.assistState = { loading: false, enabled: false };
    this.fieldDependencies = {};
  }

  /**
   * @property {string} key - The key (id) to use to uniquely identify this form field
   * @property {string} env - The environment (dev, qa, etc)
   * @property {object} schema - The schema representation of the field to render
   * @property {object} data - The saved data (if any) associated to the field
   * @property {object} context - Used to pass additional data, namely "readonly" & some internal data
   * @property {boolean} assistState - Internal state for omni assist feature
   * @property {boolean} isActive - Internal state to indicate this field is active
   * @property {array} fieldDependencies - Stores external field dependencies
   */
  static get properties() {
    return {
      key: { type: String },
      env: { type: String },
      schema: { type: Object },
      data: { type: Object },
      context: { type: Object },
      assistState: { state: true },
      isActive: { state: true },
      fieldDependencies: { state: true },
    };
  }

  static register(name = 'om2-form-field') {
    return super.register(name, Om2FormField);
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('om2-field-focus', this.onFieldFocus);
    this.addEventListener('om2-field-blur', this.onFieldBlur);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('om2-field-focus', this.onFieldFocus);
    this.removeEventListener('om2-field-blur', this.onFieldBlur);
  }

  onFieldFocus = () => {
    this.isActive = true;
  };

  onFieldBlur = () => {
    this.isActive = false;
  };

  /**
   * If a default value is defined for this field, trigger event to update parent.
   * Also if there's an override on the schema, always trigger event.
   *
   * Normally @input events are only sent when a user interacts with a field; this is
   * one exception and we add "isAutoGenerated" to the event to indicate this.
   */
  firstUpdated() {
    if (
      this.schema.override ||
      ([undefined, null, ''].includes(this.data) && this.schema.default)
    ) {
      let value = this.schema.override ?? this.schema.default;
      if (
        this.schema.enum &&
        this.schema.hasObjectValues &&
        typeof value !== 'object' &&
        value !== null
      ) {
        // If this field is configured with "hasObjectValues" but the default isn't an
        // object, try to figure out what the object is and use that.
        const match = this.schema.enum.find(
          option => option[this.schema.idProperty ?? 'id'] === value
        );
        if (match) value = match;
      }

      this.dispatchNewEvent('input', {
        detail: {
          name: this.key,
          value,
          isAutoGenerated: true,
        },
      });
    }
  }

  /**
   * Disable shadow dom for this component
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Fetch CSV or JSON enum data. Caches requests/responses so that only one request is
   * sent per unique url.
   *
   * @param {object} schema
   * @returns {array} The fetched array of options (or [] if waiting)
   */
  _getAsyncEnum(schema) {
    const key = schema.enumURL ?? schema.asyncEnum;

    const { enumPromises, enumResults, enumBlocked } = this.constructor;

    const unblockWaitingFields = () => {
      enumBlocked.get(key).forEach(field => field.requestUpdate());
    };

    const getEnumPromise = async () => {
      if (typeof schema.asyncEnum?.then === 'function') {
        enumResults.set(key, await schema.asyncEnum);
        unblockWaitingFields(); // Trigger re-render
      } else if (schema.enumURL) {
        enumResults.set(key, await fetchEnumOptions(schema.enumURL, this));
        unblockWaitingFields(); // Trigger re-render
      } else {
        throw new Error('Unknown async enum type');
      }
    };

    // Check if results are already stored
    if (enumResults.get(key)) return enumResults.get(key);

    // Initiate the fetch request if we haven't yet
    if (!enumPromises.get(key)) enumPromises.set(key, getEnumPromise());

    // Multiple fields might be blocked waiting for this promise
    if (!enumBlocked.get(key)) enumBlocked.set(key, new Set());
    enumBlocked.get(key).add(this);

    // We're waiting for options, return empty array
    return [];
  }

  /**
   * Render functionality for omni assist integration (autofill)
   *
   * @param {object} schema
   * @param {string} [icon] - The tooltip icon
   * @returns {html} The rendered output
   */
  _renderOmniAssist(schema) {
    if (schema['omni-assist'] !== 'autofill') return nothing;

    const handleClick = () => {
      const { type: embedding, prompt, confirm } = schema['omni-assist-config'];
      this.assistState.loading = true;
      this.requestUpdate();
      this.dispatchNewEvent('omni-assist-execute', {
        detail: {
          prompt,
          config: embedding,
          onSuccess: result => {
            const saveToField = () => {
              this.data = result;
              this.dispatchEvent(
                new CustomEvent('input', {
                  detail: {
                    name: this.key,
                    value: result,
                  },
                  bubbles: true,
                })
              );
            };

            this.assistState.loading = false;
            this.requestUpdate();
            if (confirm === false) {
              saveToField(); // Skip confirm modal
              return;
            }
            om2confirm(
              html` <p style="white-space: pre-wrap;">${result}</p>
                <br />
                <p class="has-text-shark is-italic">
                  * This content was not provided by Omnicom data, please use
                  responsibly.
                </p>`,
              {
                type: 'white',
                title: 'Assist',
                callback: [
                  {
                    func: saveToField,
                    label: 'Add this content',
                    cls: 'is-outlined is-small',
                  },
                ],
                hideOverlay: true,
                hideCancel: true,
                closeButton: true,
                closeOnModalClick: false,
              }
            );
          },
          onError: () => {
            this.assistState.loading = false;
            this.requestUpdate();
          },
        },
      });
    };

    if (this.isActive && !this.assistState.enabled) {
      // Before fully becoming active we need to verify that the omni-assist
      // component is available and ready.
      this.dispatchNewEvent('omni-assist-enabled', {
        detail: {
          onSuccess: () => {
            this.assistState.enabled = true;
            this.requestUpdate();
          },
        },
      });
    }

    if (
      !this.assistState.enabled ||
      (!this.isActive && !this.assistState.loading)
    )
      return nothing;

    return html`
      <omni-tooltip class="ml-1 omni-assist-icon is-pulled-right">
        ${this.assistState.loading
          ? html`<button
              slot="invoker"
              class="button is-link is-inverted is-outlined is-loading"
            ></button>`
          : html`
              <button slot="invoker" class="button is-text">
                <omni-icon
                  class="is-size-2"
                  style="fill: var(--color-primary) !important"
                  icon-id="omni:brand:omni"
                  @mousedown=${handleClick}
                  @keyup=${e => e.key === 'Enter' && e.target.mousedown()}
                >
                </omni-icon>
              </button>
            `}
        <div slot="content">Content Assist</div>
      </omni-tooltip>
    `;
  }

  /**
   * The main implementation method used by render(). This method is recursive and needs
   * to take an argument, so needed to be separated out from the standard lit-element
   * render() method. It renders one particular schema object, working recursively
   * through nested schemas.
   *
   * @param {object} schema
   * @returns {html} The rendered output
   */
  _renderImpl(schema) {
    /**
     * Clone an object with the exception of a particular property name
     *
     * @param {object} obj
     * @param {string} exceptProperty
     */
    const cloneExcept = (obj, ...exceptProperty) => {
      const copy = { ...obj }; // shallow copy is fine here
      exceptProperty.forEach(prop => delete copy[prop]);
      return copy;
    };

    // determines title text; checks if required and adds * in front of title
    const renderTitleText = () => {
      const normalized = schema.title === ' ' ? html`&nbsp;` : schema.title;
      return html`${schema.required ? '* ' : ''}${normalized}`;
    };

    // Handle title labels (for any field type)
    if (schema.title) {
      return html`
        <div class="mb-2 ${this.context?.titleHidden ? 'is-hidden' : ''}">
          <span class="subtitle"> ${renderTitleText()} </span>
          ${renderTooltip(schema)} ${this._renderOmniAssist(schema)}
        </div>
        ${this._renderImpl(
          cloneExcept(schema, 'title', 'tooltip', 'description')
        )}
      `;
    }

    // Handle errors (for any field type)
    if (schema.error) {
      return html`
        <div class="error mt-1">
          ${this._renderImpl(cloneExcept(schema, 'error'))}
          <div class="message mt-2">
            <omni-icon
              class="is-inline-block is-size-2 ml-4 mr-2"
              icon-id="omni:informative:error"
            ></omni-icon>
            <span>${schema.error}</span>
          </div>
        </div>
      `;
    }

    // When enumURL is in the schema (& asycEnum), handle fetching the options
    if (schema.asyncEnum && schema.enumURL) {
      const searchToken = '%SEARCH%';

      // Check if there are unresolved field value references in the enumURL
      if (schema.enumURL.match(/%:[^%]+%/)) {
        const field = schema.enumURL.match(/%:([^%]+)%/)[1];
        const value = this.fieldDependencies[field];
        if (value !== undefined && value !== null && value.length !== 0) {
          // Replace first unresolved field in url, and recurse with updated url
          const updatedURL = schema.enumURL.replace(/%:([^%]+)%/, value);
          return this._renderImpl({ ...schema, enumURL: updatedURL });
        }
        this.dispatchNewEvent('om2-add-field-change-listener', {
          detail: {
            field,
            callback: (k, v) => {
              // If the dependant value changes (from a previously !undefined value) reset the current field's state.
              if (this.fieldDependencies[k] !== undefined) {
                this.dispatchNewEvent('input', {
                  detail: {
                    name: this.key,
                    value: undefined,
                    isAutoGenerated: true,
                  },
                });
              }
              this.fieldDependencies = { ...this.fieldDependencies, [k]: v };
            },
          },
        });

        return html`<input disabled class="input is-fullwidth" style="height: 35px"></input>`;
      }

      if (schema.enumURL.includes(searchToken)) {
        // If the url includes search token, it is only for searching (not all options)
        const searchFunc = async search => {
          const url = schema.enumURL.replace(searchToken, search);
          return fetchEnumOptions(url, this);
        };
        // eslint-disable-next-line no-param-reassign
        schema = {
          ...schema,
          asyncEnum: false,
          enum: [],
          asyncSearch: searchFunc,
          hasObjectValues: true, // Without this, loading saved data would only show ids
        };
      } else {
        // Normal async url, pull all the options
        // eslint-disable-next-line no-param-reassign
        schema = { ...schema, enum: this._getAsyncEnum(schema) };
      }
    }

    // If a promise object exists as asyncEnum property, resolve it and set as enum
    if (typeof schema?.asyncEnum?.then === 'function') {
      // eslint-disable-next-line no-param-reassign
      schema = { ...schema, enum: this._getAsyncEnum(schema) };
    }

    // If enum options are loading just use skeleton placeholder
    if (schema.asyncEnum && !(schema.enum?.length > 0)) {
      return html`<div
        class="skeleton is-fullwidth"
        style="height: 35px"
      ></div>`;
    }

    return Om2FormFieldBase.fieldTemplate({
      schema,
      key: this.key,
      data: this.data,
      env: this.env,
      context: this.context,
    });
  }

  /**
   * Standard render method that calls our recursive render on the root schema.
   */
  render() {
    return this._renderImpl(this.schema);
  }
}

// Static fields so we can cache results of fetching urls
Om2FormField.enumPromises = new Map();
Om2FormField.enumResults = new Map();
Om2FormField.enumBlocked = new Map();

Om2FormField.register();
